import { useCallback, useEffect, useState } from 'react';
import type { Pago } from '../types';
import { storage } from '../lib/storage';
import { cancelarNotificacionDePago, programarNotificacionPago } from '../lib/notifications';
import { encolar } from '../lib/outbox';
import { onDatosCambiados } from '../lib/events';

function extraerCamposParaSync(pago: Pago): Record<string, unknown> {
  return {
    destinatario: pago.destinatario,
    monto: pago.monto,
    fechaLimite: pago.fechaLimite,
  };
}

export function usePagos() {
  const [pagos, setPagos] = useState<Pago[]>([]);
  const [cargando, setCargando] = useState(true);

  const recargar = useCallback(async () => {
    const lista = await storage.obtenerPagos();
    setPagos(lista);
  }, []);

  useEffect(() => {
    void (async () => {
      await recargar();
      setCargando(false);
    })();
    return onDatosCambiados(() => {
      void recargar();
    });
  }, [recargar]);

  const guardarPago = useCallback(
    async (pago: Pago, esCreate: boolean) => {
      const ahora = new Date().toISOString();
      const conTimestamp: Pago = { ...pago, updatedAt: ahora };
      await storage.guardarPago(conTimestamp);
      await encolar({
        entity: 'pago',
        id: pago.id,
        type: esCreate ? 'create' : 'update',
        data: extraerCamposParaSync(conTimestamp),
        clientUpdatedAt: ahora,
      });
      void programarNotificacionPago(conTimestamp);
      await recargar();
    },
    [recargar]
  );

  // Marcar como realizado = borrar de la lista (es solo un recordatorio, no
  // necesita historial). Cancela la notificación y encola el delete para sync.
  const marcarRealizado = useCallback(
    async (id: string) => {
      try {
        await cancelarNotificacionDePago(id);
      } catch (error) {
        console.error('Error al cancelar notificación de pago:', error);
      }
      await storage.eliminarPago(id);
      await encolar({
        entity: 'pago',
        id,
        type: 'delete',
        clientUpdatedAt: new Date().toISOString(),
      });
      await recargar();
    },
    [recargar]
  );

  return { pagos, cargando, guardarPago, marcarRealizado };
}
